import type { ParticipantFlagCategory, ParticipantFlagWithDetails } from '../types';

// ============================================================================
// Flag Severity Levels
// ============================================================================

export type FlagSeverity = 'high' | 'medium' | 'low';

export const FLAG_SEVERITY = {
  high: {
    categories: ['coach_flag'] as ParticipantFlagCategory[],
    row: { background: '#FFDFDF' },
    pill: { background: 'transparent', border: '#D64545', text: '#D64545' },
  },
  medium: {
    categories: ['attendance', 'participant_feedback'] as ParticipantFlagCategory[],
    row: { background: '#FEF3E7' },
    pill: { background: '#FEF3E7', border: '#E07B4C', text: '#E07B4C' },
  },
  low: {
    categories: ['engagement'] as ParticipantFlagCategory[],
    row: { background: '#FEFDF6' },
    pill: { background: 'transparent', border: '#F9A825', text: '#B8860B' },
  },
} as const;

const SEVERITY_ORDER: Record<FlagSeverity, number> = { high: 3, medium: 2, low: 1 };

/**
 * Derive severity from a flag's category.
 */
export function getFlagSeverity(flagCategory: ParticipantFlagCategory): FlagSeverity {
  if (flagCategory === 'coach_flag') return 'high';
  if (flagCategory === 'attendance' || flagCategory === 'participant_feedback') return 'medium';
  return 'low'; // engagement
}

/**
 * Get the highest severity among a set of flags.
 */
export function getHighestSeverity(flags: ParticipantFlagWithDetails[]): FlagSeverity {
  return flags.reduce<FlagSeverity>((highest, flag) => {
    const current = getFlagSeverity(flag.flag_category);
    return SEVERITY_ORDER[current] > SEVERITY_ORDER[highest] ? current : highest;
  }, 'low');
}

/**
 * Sort comparator: sorts by highest severity descending (high first).
 */
export function compareBySeverity(
  aFlags: ParticipantFlagWithDetails[],
  bFlags: ParticipantFlagWithDetails[],
): number {
  const order: Record<FlagSeverity, number> = { high: 0, medium: 1, low: 2 };
  return order[getHighestSeverity(aFlags)] - order[getHighestSeverity(bFlags)];
}
